import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useChampionship } from "@/context/ChampionshipContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import { Plus, Trash2, Pencil } from "lucide-react";

const blank = {
  title: "", excerpt: "", content_html: "", image_path: "", is_featured: false, is_published: true,
};

export default function AdminNewsPage() {
  const { current } = useChampionship() || {};
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = useCallback(async () => {
    if (!current?.id) return;
    const { data } = await api.get(`/championships/${current.id}/news`);
    setItems(data || []);
  }, [current?.id]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (n) => {
    setEditing(n);
    setForm({
      title: n.title, excerpt: n.excerpt || "", content_html: n.content_html || "",
      image_path: n.image_path || "", is_featured: !!n.is_featured, is_published: !!n.is_published,
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) await api.patch(`/news/${editing.id}`, form);
      else await api.post(`/championships/${current.id}/news`, form);
      toast.success("Saved");
      setOpen(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this article?")) return;
    try { await api.delete(`/news/${id}`); toast.success("Deleted"); await load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-8 lg:p-10" data-testid="admin-news">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">News</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mt-2">News & blog posts</h1>
        </div>
        <Button onClick={openNew} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="news-new-button">
          <Plus className="h-4 w-4 mr-2" /> New article
        </Button>
      </div>

      <div className="border border-border bg-white">
        {items.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground">No articles yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <tr>
                <th className="p-3">Title</th>
                <th className="p-3">Type</th>
                <th className="p-3">Published</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((n) => (
                <tr key={n.id} className="border-t border-border" data-testid={`news-row-${n.id}`}>
                  <td className="p-3 font-medium">{n.title}</td>
                  <td className="p-3">{n.is_featured ? "Featured" : "Blog"}</td>
                  <td className="p-3 text-muted-foreground">{new Date(n.published_at).toLocaleDateString()}</td>
                  <td className="p-3 text-right space-x-2">
                    <button onClick={() => openEdit(n)} className="text-primary" data-testid={`news-edit-${n.id}`}><Pencil className="h-4 w-4 inline" /></button>
                    <button onClick={() => remove(n.id)} className="text-destructive" data-testid={`news-delete-${n.id}`}><Trash2 className="h-4 w-4 inline" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit article" : "New article"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="news-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Excerpt</Label>
              <Textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="news-excerpt-input" />
            </div>
            <ImageUpload label="Cover image" value={form.image_path} onChange={(v) => setForm({ ...form, image_path: v })} />
            <div className="space-y-2">
              <Label>Content (HTML allowed)</Label>
              <Textarea rows={10} value={form.content_html} onChange={(e) => setForm({ ...form, content_html: e.target.value })} data-testid="news-content-input" />
            </div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={form.is_featured} onCheckedChange={(v) => setForm({ ...form, is_featured: !!v })} data-testid="news-featured-check" />
                Permanent / Featured
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: !!v })} data-testid="news-published-check" />
                Published
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="news-save-button">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
